/**
 * Trades Poster — Keywords
 * Document type: Source register
 * Status: READY — Keywords register specification
 *
 * Creates, displays, saves and retains Keyword records.
 * Pool Keywords belong to a Trade Category; Subcategory Keywords belong to a
 * Subcategory. Edit permits spelling corrections only. Saved records are retained.
 */

import { buildSampleTradeCategories } from '../sections/AdminDashboard/TradeCategories/tradeCategoriesDummyData';
import { getTradeSubcategories, trimName as trimTradeName } from './tradeCategoryWorkflow';
import { recordChangeHistory } from './changeHistoryStore';
import { CHANGE_HISTORY_DISPLAY } from './changeHistoryEntry';

const STOP_WORDS = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'in', 'of', 'to', 'with']);

export const KEYWORD_ROUTE = '/admin/keywords';
export const DEFAULT_ACTOR = 'Saim Sahi';

export const FORM_MODE = {
  ADD: 'add',
  VIEW: 'view',
  EDIT: 'edit',
};

export const KEYWORD_STATUS = {
  active: 'Active',
  inactive: 'Inactive',
};

export const ID_PLACEHOLDER = 'Generated after Save';

export const EMPTY_TABLE_MESSAGE = 'No Keywords have been added.';

export const LIST_PREVIEW_COUNT = 2;

export const SAVE_MESSAGES = {
  createdSingle: 'Keyword created successfully.',
  createdBatch: 'Keywords created successfully.',
  updated: 'Keywords updated successfully.',
  failure: 'Keywords could not be saved. Review the highlighted fields and try again.',
  updateFailure: 'Keywords could not be updated. Review the highlighted fields and try again.',
  /** @deprecated Status handoff not part of Keywords register Save. */
  activated: 'Keyword activated successfully.',
  /** @deprecated Status handoff not part of Keywords register Save. */
  inactivated: 'Keyword made Inactive successfully.',
};

export const VALIDATION_MESSAGES = {
  tradeCategoryRequired: 'Select a trade category.',
  subcategoryRequired: 'Select a subcategory.',
  subcategoryWrongCategory: 'Select a subcategory from this trade category.',
  subcategoryDuplicate: 'This subcategory is already selected.',
  keywordRequired: 'Enter a keyword name.',
  atLeastOneKeyword: 'Add at least one keyword.',
  poolKeywordDuplicate: 'This keyword already exists in this trade category.',
  keywordDuplicate: 'This keyword already exists in this subcategory.',
  spellingOnly: 'Only spelling corrections are allowed.',
  concurrent: 'These records have changed. Review the latest records.',
  /** @deprecated Prefer keywordRequired / spellingOnly */
  keywordInvalid: 'Enter a keyword name.',
  /** @deprecated Prefer poolKeywordDuplicate */
  poolKeywordRequired: 'Enter a keyword name.',
  poolKeywordInvalid: 'Enter a keyword name.',
  meaningChanged: 'Only spelling corrections are allowed.',
  atLeastOnePoolKeyword: 'Add at least one keyword.',
  atLeastOneSubcategory: 'Select a subcategory.',
  addKeywordsRequired: 'Add at least one keyword.',
};

export const FILTER_ALL = 'all';

export const STATUS_FILTER_OPTIONS = [
  { value: FILTER_ALL, label: 'All' },
  { value: KEYWORD_STATUS.active, label: 'Active' },
  { value: KEYWORD_STATUS.inactive, label: 'Inactive' },
];

export const trimKeyword = (value) => (value || '').trim().replace(/\s+/g, ' ');

export const normKeyword = (value) => trimKeyword(value).toLowerCase();

export const formatKeywordAuditDate = (date = new Date()) =>
  date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const formatKeywordListDate = (value) => {
  if (!value) return '—';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '—';
    return d.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return typeof value === 'string' ? value : '—';
  }
};

/** Parse display or ISO date strings for grouping comparisons. */
export function parseKeywordDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct;
  const normalised = raw
    .replace(/\bat\b/i, '')
    .replace(/,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const fallback = new Date(normalised);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function earliestDateValue(values = []) {
  let best = null;
  let bestMs = Infinity;
  values.forEach((value) => {
    const parsed = parseKeywordDate(value);
    if (!parsed) return;
    const ms = parsed.getTime();
    if (ms < bestMs) {
      bestMs = ms;
      best = value;
    }
  });
  return best || '';
}

function latestDateValue(values = []) {
  let best = null;
  let bestMs = -Infinity;
  values.forEach((value) => {
    const parsed = parseKeywordDate(value);
    if (!parsed) return;
    const ms = parsed.getTime();
    if (ms > bestMs) {
      bestMs = ms;
      best = value;
    }
  });
  return best || '';
}

export function formatSubcategoryLabel(_subcategoryId, subcategoryName) {
  return trimKeyword(subcategoryName) || '—';
}

export function emptyKeywordRecord() {
  return {
    id: '',
    keyword: '',
    tradeCategoryId: '',
    tradeCategoryName: '',
    subcategoryId: '',
    subcategoryName: '',
    isPool: false,
    status: KEYWORD_STATUS.active,
    activePostCount: 0,
    soleActiveKeywordPostCount: 0,
    createdOn: '',
    createdBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
  };
}

export function emptyKeywordBlock(overrides = {}) {
  return {
    localKey: `kw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: '',
    keyword: '',
    ...overrides,
  };
}

export function createInitialKeywordBlocks(count = 1) {
  return Array.from({ length: count }, () => emptyKeywordBlock());
}

export function emptySubcategorySection(overrides = {}) {
  return {
    localKey: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    subcategoryId: '',
    subcategoryName: '',
    keywords: [],
    ...overrides,
  };
}

export function createInitialSubcategorySections(count = 0) {
  return Array.from({ length: Math.max(0, count) }, () => emptySubcategorySection());
}

export function keywordNamesToBlocks(names = []) {
  return (names || [])
    .map((name) => trimKeyword(name))
    .filter(Boolean)
    .map((keyword) => emptyKeywordBlock({ keyword }));
}

export function recordsToKeywordBlocks(records = []) {
  return sortKeywordRecords(records).map((record) =>
    emptyKeywordBlock({
      id: record.id || '',
      keyword: record.keyword || '',
    }),
  );
}

export function cloneKeywordRecord(record) {
  return {
    ...emptyKeywordRecord(),
    ...record,
  };
}

export function getKeywordCatalog() {
  return buildSampleTradeCategories().map((cat) => ({
    id: cat.id,
    name: cat.name,
    subcategories: getTradeSubcategories(cat).map((sub) => ({
      id: sub.id,
      name: trimTradeName(sub.name),
    })),
  }));
}

export function getTradeCategoryOptions(catalog = getKeywordCatalog()) {
  return [...catalog].sort((a, b) => a.name.localeCompare(b.name));
}

export function getSubcategoriesForTradeCategory(tradeCategoryId, catalog = getKeywordCatalog()) {
  const category = catalog.find((c) => c.id === tradeCategoryId);
  return [...(category?.subcategories || [])].sort((a, b) => a.name.localeCompare(b.name));
}

export function resolveTradeCategoryName(tradeCategoryId, catalog = getKeywordCatalog()) {
  return catalog.find((c) => c.id === tradeCategoryId)?.name || '';
}

export function resolveSubcategory(tradeCategoryId, subcategoryId, catalog = getKeywordCatalog()) {
  return getSubcategoriesForTradeCategory(tradeCategoryId, catalog).find((s) => s.id === subcategoryId) || null;
}

export function getAllKeywords(keywords = []) {
  return [...(keywords || [])];
}

function sortKeywordRecords(records = []) {
  return [...records].sort((a, b) => normKeyword(a.keyword).localeCompare(normKeyword(b.keyword)));
}

export function keywordPreviewRows(records = []) {
  return sortKeywordRecords(records).map((record) => ({
    id: record.id || '—',
    name: trimKeyword(record.keyword),
  }));
}

export function keywordPreviewNames(records = []) {
  return keywordPreviewRows(records).map((row) => row.name);
}

export function subcategoryPreviewRows(subcategories = []) {
  return [...(subcategories || [])]
    .sort((a, b) => trimKeyword(a.name).localeCompare(trimKeyword(b.name)))
    .map((sub) => ({
      id: sub.id || '—',
      name: trimKeyword(sub.name) || '—',
    }));
}

export function subcategoryPreviewNames(subcategories = []) {
  return subcategoryPreviewRows(subcategories).map((row) => row.name);
}

export function buildKeywordListGroups(keywords = []) {
  const all = getAllKeywords(keywords);
  const byCategory = new Map();

  all.forEach((record) => {
    const categoryKey = record.tradeCategoryId || record.tradeCategoryName || 'unknown';
    if (!byCategory.has(categoryKey)) {
      byCategory.set(categoryKey, {
        tradeCategoryId: record.tradeCategoryId || '',
        tradeCategoryName: record.tradeCategoryName || '',
        poolKeywords: [],
        subcategories: new Map(),
        keywords: [],
        allRecords: [],
      });
    }
    const category = byCategory.get(categoryKey);
    category.allRecords.push(record);
    if (record.isPool) {
      category.poolKeywords.push(record);
      return;
    }
    const subKey = record.subcategoryId || record.subcategoryName || 'none';
    if (!category.subcategories.has(subKey)) {
      category.subcategories.set(subKey, {
        id: record.subcategoryId || '',
        name: record.subcategoryName || '',
      });
    }
    category.keywords.push(record);
  });

  return [...byCategory.values()]
    .sort((a, b) => (a.tradeCategoryName || '').localeCompare(b.tradeCategoryName || ''))
    .map((category) => {
      const poolKeywords = sortKeywordRecords(category.poolKeywords);
      const keywordsForCategory = sortKeywordRecords(category.keywords);
      const subcategories = [...category.subcategories.values()].sort((a, b) =>
        trimKeyword(a.name).localeCompare(trimKeyword(b.name)),
      );
      const createdOn = earliestDateValue(category.allRecords.map((r) => r.createdOn));
      const lastUpdatedOn = latestDateValue(
        category.allRecords.map((r) => r.lastUpdatedOn || r.createdOn),
      );
      return {
        groupKey: category.tradeCategoryId || category.tradeCategoryName,
        tradeCategoryId: category.tradeCategoryId,
        tradeCategoryName: category.tradeCategoryName,
        poolKeywords,
        subcategories,
        keywords: keywordsForCategory,
        createdOn,
        lastUpdatedOn,
        /** Snapshot for concurrency checks when opening Edit. */
        contentStamp: lastUpdatedOn || createdOn || '',
      };
    });
}

export function findKeywordListGroup(keywords = [], tradeCategoryId) {
  if (!tradeCategoryId) return null;
  return (
    buildKeywordListGroups(keywords).find((group) => group.tradeCategoryId === tradeCategoryId) ||
    null
  );
}

export function buildCategoryKeywordForm(group, tradeCategoryId = '', tradeCategoryName = '') {
  if (!group) {
    return {
      tradeCategoryId: tradeCategoryId || '',
      tradeCategoryName: tradeCategoryName || '',
      poolBlocks: [],
      subcategorySections: [],
      createdOn: '',
      lastUpdatedOn: '',
      contentStamp: '',
    };
  }

  const bySub = new Map();
  (group.keywords || []).forEach((record) => {
    const key = record.subcategoryId || record.subcategoryName || 'none';
    if (!bySub.has(key)) {
      bySub.set(key, {
        localKey: `sub-${key}`,
        subcategoryId: record.subcategoryId || '',
        subcategoryName: record.subcategoryName || '',
        keywords: [],
      });
    }
    bySub.get(key).keywords.push(record);
  });

  return {
    tradeCategoryId: group.tradeCategoryId || tradeCategoryId || '',
    tradeCategoryName: group.tradeCategoryName || tradeCategoryName || '',
    poolBlocks: recordsToKeywordBlocks(group.poolKeywords || []),
    subcategorySections: [...bySub.values()]
      .sort((a, b) => trimKeyword(a.subcategoryName).localeCompare(trimKeyword(b.subcategoryName)))
      .map((section) => ({
        ...section,
        keywords: recordsToKeywordBlocks(section.keywords),
      })),
    createdOn: group.createdOn || '',
    lastUpdatedOn: group.lastUpdatedOn || '',
    contentStamp: group.contentStamp || group.lastUpdatedOn || group.createdOn || '',
  };
}

/** @deprecated Use getAllKeywords — Active and Inactive remain displayed. */
export function getCurrentKeywords(keywords = []) {
  return getAllKeywords(keywords);
}

export function sortKeywordsAlphabetically(keywords = []) {
  return [...keywords].sort((a, b) => normKeyword(a.keyword).localeCompare(normKeyword(b.keyword)));
}

export function matchesKeywordSearch(record, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return (
    (record.id && record.id.toLowerCase().includes(q)) ||
    (record.keyword && record.keyword.toLowerCase().includes(q))
  );
}

export function matchesKeywordFilters(record, { tradeCategoryFilter, subcategoryFilter, statusFilter } = {}) {
  if (tradeCategoryFilter && tradeCategoryFilter !== FILTER_ALL) {
    if (record.tradeCategoryId !== tradeCategoryFilter) return false;
  }
  if (subcategoryFilter && subcategoryFilter !== FILTER_ALL) {
    if (record.subcategoryId !== subcategoryFilter) return false;
  }
  if (statusFilter && statusFilter !== FILTER_ALL) {
    if (record.status !== statusFilter) return false;
  }
  return true;
}

export function filterKeywords(keywords = [], { searchQuery, tradeCategoryFilter, subcategoryFilter, statusFilter } = {}) {
  return sortKeywordsAlphabetically(
    getAllKeywords(keywords).filter(
      (record) =>
        matchesKeywordSearch(record, searchQuery) &&
        matchesKeywordFilters(record, { tradeCategoryFilter, subcategoryFilter, statusFilter }),
    ),
  );
}

export function isValidKeywordWording(value) {
  const trimmed = trimKeyword(value);
  if (!trimmed) return false;
  if (trimmed.length < 2 || trimmed.length > 120) return false;
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  if (/^[\d\s\-_.]+$/.test(trimmed)) return false;
  return true;
}

function keywordTokens(value) {
  return normKeyword(value)
    .replace(/[^\w\s'-]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length >= 2 && !STOP_WORDS.has(word));
}

function levenshteinRatio(a, b) {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;

  const matrix = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  const distance = matrix[a.length][b.length];
  return 1 - distance / Math.max(a.length, b.length);
}

export function isMinorKeywordCorrection(baseline, next) {
  const a = normKeyword(baseline);
  const b = normKeyword(next);
  if (a === b) return true;

  const tokensA = keywordTokens(baseline);
  const tokensB = keywordTokens(next);
  const setA = new Set(tokensA);
  const setB = new Set(tokensB);

  if (tokensA.length && tokensA.length === tokensB.length && tokensA.every((token) => setB.has(token))) {
    return true;
  }

  const ratio = levenshteinRatio(a, b);
  if (ratio >= 0.82) return true;

  const symmetricDiff = [
    ...tokensA.filter((token) => !setB.has(token)),
    ...tokensB.filter((token) => !setA.has(token)),
  ];
  if (symmetricDiff.length <= 1 && ratio >= 0.65) return true;

  return false;
}

export function editsKeywordMeaning(baseline, next) {
  if (!baseline?.keyword || !next) return false;
  if (normKeyword(baseline.keyword) === normKeyword(next)) return false;
  return !isMinorKeywordCorrection(baseline.keyword, next);
}

export function keywordFormHasChanges(formData, baseline) {
  return trimKeyword(formData?.keyword) !== trimKeyword(baseline?.keyword);
}

export function nextKeywordId(keywords = []) {
  const nums = keywords
    .map((k) => {
      const match = /^KEY-(\d+)$/i.exec(k.id || '');
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `KEY-${String(next).padStart(4, '0')}`;
}

export function getCompletedKeywordBlocks(blocks = []) {
  return (blocks || []).filter((block) => trimKeyword(block.keyword));
}

export function isDuplicateUnderSubcategory(keywordName, subcategoryId, keywords = [], { excludeId = null } = {}) {
  const normalised = normKeyword(keywordName);
  if (!normalised || !subcategoryId) return false;
  return (keywords || []).some(
    (k) =>
      !k.isPool &&
      k.subcategoryId === subcategoryId &&
      (!excludeId || k.id !== excludeId) &&
      normKeyword(k.keyword) === normalised,
  );
}

export function isDuplicatePoolKeyword(keywordName, tradeCategoryId, keywords = [], { excludeId = null } = {}) {
  const normalised = normKeyword(keywordName);
  if (!normalised || !tradeCategoryId) return false;
  return (keywords || []).some(
    (k) =>
      k.isPool &&
      k.tradeCategoryId === tradeCategoryId &&
      (!excludeId || k.id !== excludeId) &&
      normKeyword(k.keyword) === normalised,
  );
}

function collectBlocksFieldErrors({
  blocks = [],
  errorPrefix,
  requiredMessage,
  duplicateMessage,
  isDuplicate,
  requireCompleted = false,
  atLeastOneMessage,
  formErrorKey,
  checkSpelling = null,
}) {
  const errors = {};
  const completed = getCompletedKeywordBlocks(blocks);

  if (requireCompleted && completed.length === 0) {
    if (formErrorKey && atLeastOneMessage) {
      errors[formErrorKey] = atLeastOneMessage;
    }
    const firstKey = blocks[0]?.localKey;
    if (firstKey) {
      errors[`${errorPrefix}${firstKey}`] = requiredMessage;
    }
    return errors;
  }

  const seenInBatch = new Map();

  blocks.forEach((block) => {
    const raw = block.keyword || '';
    const trimmed = trimKeyword(raw);
    const errKey = `${errorPrefix}${block.localKey}`;

    if (!trimmed) {
      if (raw.length > 0 || block.id) {
        errors[errKey] = requiredMessage;
      }
      return;
    }

    if (checkSpelling) {
      const spellingError = checkSpelling(block, trimmed);
      if (spellingError) {
        errors[errKey] = spellingError;
        return;
      }
    }

    const normalised = normKeyword(trimmed);
    if (seenInBatch.has(normalised)) {
      errors[errKey] = duplicateMessage;
      return;
    }
    seenInBatch.set(normalised, block.localKey);

    if (isDuplicate(trimmed, block)) {
      errors[errKey] = duplicateMessage;
    }
  });

  return errors;
}

/**
 * Add-mode validation for Keyword blocks (Pool + Subcategory sections).
 */
export function collectAddKeywordFieldErrors(
  {
    tradeCategoryId,
    subcategorySections = [],
    poolBlocks = [],
  },
  existingKeywords = [],
  catalog = getKeywordCatalog(),
) {
  const errors = {};

  if (!tradeCategoryId) {
    errors.tradeCategoryId = VALIDATION_MESSAGES.tradeCategoryRequired;
  }

  const completedPool = getCompletedKeywordBlocks(poolBlocks);
  const validSubs = new Set(
    getSubcategoriesForTradeCategory(tradeCategoryId, catalog).map((s) => s.id),
  );
  const seenSubcategories = new Map();
  let anyCompletedKeyword = completedPool.length > 0;

  if (poolBlocks.length > 0) {
    Object.assign(
      errors,
      collectBlocksFieldErrors({
        blocks: poolBlocks,
        errorPrefix: 'pool-',
        requiredMessage: VALIDATION_MESSAGES.keywordRequired,
        duplicateMessage: VALIDATION_MESSAGES.poolKeywordDuplicate,
        isDuplicate: (trimmed) =>
          Boolean(tradeCategoryId) &&
          isDuplicatePoolKeyword(trimmed, tradeCategoryId, existingKeywords),
      }),
    );
  }

  subcategorySections.forEach((section) => {
    const subErrKey = `sub-${section.localKey}`;
    const completedInSection = getCompletedKeywordBlocks(section.keywords);
    const hasEnteredKeyword = (section.keywords || []).some(
      (block) => trimKeyword(block.keyword) || String(block.keyword || '').length > 0,
    );

    if (completedInSection.length > 0 || hasEnteredKeyword) {
      if (!section.subcategoryId) {
        errors[subErrKey] = VALIDATION_MESSAGES.subcategoryRequired;
      } else if (tradeCategoryId && !validSubs.has(section.subcategoryId)) {
        errors[subErrKey] = VALIDATION_MESSAGES.subcategoryWrongCategory;
      } else if (seenSubcategories.has(section.subcategoryId)) {
        errors[subErrKey] = VALIDATION_MESSAGES.subcategoryDuplicate;
      } else if (section.subcategoryId) {
        seenSubcategories.set(section.subcategoryId, section.localKey);
      }
    } else if (section.subcategoryId) {
      if (seenSubcategories.has(section.subcategoryId)) {
        errors[subErrKey] = VALIDATION_MESSAGES.subcategoryDuplicate;
      } else {
        seenSubcategories.set(section.subcategoryId, section.localKey);
      }
    }

    if (completedInSection.length > 0) {
      anyCompletedKeyword = true;
    }

    Object.assign(
      errors,
      collectBlocksFieldErrors({
        blocks: section.keywords || [],
        errorPrefix: `kw-${section.localKey}-`,
        requiredMessage: VALIDATION_MESSAGES.keywordRequired,
        duplicateMessage: VALIDATION_MESSAGES.keywordDuplicate,
        isDuplicate: (trimmed) =>
          Boolean(section.subcategoryId) &&
          isDuplicateUnderSubcategory(trimmed, section.subcategoryId, existingKeywords),
      }),
    );
  });

  if (!anyCompletedKeyword) {
    errors._form = VALIDATION_MESSAGES.atLeastOneKeyword;
  }

  return errors;
}

/**
 * Edit-mode validation for all saved Keywords in a category grouping.
 */
export function collectCategoryEditKeywordFieldErrors(
  form,
  { existingKeywords = [], baselineForm = null, latestGroup = null } = {},
) {
  const errors = {};

  if (
    baselineForm?.contentStamp &&
    latestGroup &&
    String(latestGroup.contentStamp || latestGroup.lastUpdatedOn || '') !==
      String(baselineForm.contentStamp)
  ) {
    errors._form = VALIDATION_MESSAGES.concurrent;
  }

  const tradeCategoryId = form.tradeCategoryId;
  const baselineById = new Map();
  (baselineForm?.poolBlocks || []).forEach((block) => {
    if (block.id) baselineById.set(block.id, block);
  });
  (baselineForm?.subcategorySections || []).forEach((section) => {
    (section.keywords || []).forEach((block) => {
      if (block.id) baselineById.set(block.id, block);
    });
  });

  Object.assign(
    errors,
    collectBlocksFieldErrors({
      blocks: form.poolBlocks || [],
      errorPrefix: 'pool-',
      requiredMessage: VALIDATION_MESSAGES.keywordRequired,
      duplicateMessage: VALIDATION_MESSAGES.poolKeywordDuplicate,
      isDuplicate: (trimmed, block) =>
        Boolean(tradeCategoryId) &&
        isDuplicatePoolKeyword(trimmed, tradeCategoryId, existingKeywords, {
          excludeId: block.id,
        }),
      checkSpelling: (block, trimmed) => {
        const baseline = baselineById.get(block.id);
        if (baseline && editsKeywordMeaning({ keyword: baseline.keyword }, trimmed)) {
          return VALIDATION_MESSAGES.spellingOnly;
        }
        return null;
      },
    }),
  );

  (form.subcategorySections || []).forEach((section) => {
    Object.assign(
      errors,
      collectBlocksFieldErrors({
        blocks: section.keywords || [],
        errorPrefix: `kw-${section.localKey}-`,
        requiredMessage: VALIDATION_MESSAGES.keywordRequired,
        duplicateMessage: VALIDATION_MESSAGES.keywordDuplicate,
        isDuplicate: (trimmed, block) =>
          Boolean(section.subcategoryId) &&
          isDuplicateUnderSubcategory(trimmed, section.subcategoryId, existingKeywords, {
            excludeId: block.id,
          }),
        checkSpelling: (block, trimmed) => {
          const baseline = baselineById.get(block.id);
          if (baseline && editsKeywordMeaning({ keyword: baseline.keyword }, trimmed)) {
            return VALIDATION_MESSAGES.spellingOnly;
          }
          return null;
        },
      }),
    );
  });

  return errors;
}

/** @deprecated Prefer collectCategoryEditKeywordFieldErrors for category Edit. */
export function collectEditKeywordFieldErrors(formData, { baseline = null, existingKeywords = [] } = {}) {
  const errors = {};
  const trimmed = trimKeyword(formData?.keyword);

  if (!trimmed) {
    errors.keyword = VALIDATION_MESSAGES.keywordRequired;
  } else if (baseline && editsKeywordMeaning(baseline, trimmed)) {
    errors.keyword = VALIDATION_MESSAGES.spellingOnly;
  } else if (formData?.isPool || (!formData?.subcategoryId && formData?.isPool !== false)) {
    if (
      formData?.tradeCategoryId &&
      isDuplicatePoolKeyword(trimmed, formData.tradeCategoryId, existingKeywords, {
        excludeId: formData.id,
      })
    ) {
      errors.keyword = VALIDATION_MESSAGES.poolKeywordDuplicate;
    }
  } else if (
    formData?.subcategoryId &&
    isDuplicateUnderSubcategory(trimmed, formData.subcategoryId, existingKeywords, {
      excludeId: formData.id,
    })
  ) {
    errors.keyword = VALIDATION_MESSAGES.keywordDuplicate;
  }

  return errors;
}

/** @deprecated Prefer collectEditKeywordFieldErrors / collectAddKeywordFieldErrors */
export function collectKeywordFieldErrors(formData, options = {}) {
  return collectEditKeywordFieldErrors(formData, options);
}

/** Remove an unsaved Keyword block, or clear name when it is the last block. */
export function removeOrClearKeywordBlock(blocks = [], localKey) {
  const list = [...(blocks || [])];
  const index = list.findIndex((block) => block.localKey === localKey);
  if (index < 0) return list;
  if (list[index].id) return list;
  if (list.length <= 1) {
    return [{ ...list[0], keyword: '', id: '' }];
  }
  list.splice(index, 1);
  return list;
}

export function canActivateKeyword(record) {
  return Boolean(record?.id) && record.status === KEYWORD_STATUS.inactive;
}

export function canMakeKeywordInactive(record) {
  return Boolean(record?.id) && record.status === KEYWORD_STATUS.active;
}

export function processKeywordActivate(keywords, keywordId, actor = DEFAULT_ACTOR) {
  const stamp = formatKeywordAuditDate();
  return keywords.map((k) =>
    k.id === keywordId
      ? {
          ...k,
          status: KEYWORD_STATUS.active,
          lastUpdatedOn: stamp,
          lastUpdatedBy: actor,
        }
      : k,
  );
}

export function processKeywordMakeInactive(keywords, keywordId, actor = DEFAULT_ACTOR) {
  const stamp = formatKeywordAuditDate();
  return keywords.map((k) =>
    k.id === keywordId
      ? {
          ...k,
          status: KEYWORD_STATUS.inactive,
          lastUpdatedOn: stamp,
          lastUpdatedBy: actor,
        }
      : k,
  );
}

function appendCreatedKeywords(working, created, blocks, fields, stamp, actor) {
  getCompletedKeywordBlocks(blocks).forEach((block) => {
    const id = nextKeywordId(working);
    const saved = {
      ...emptyKeywordRecord(),
      id,
      keyword: trimKeyword(block.keyword),
      ...fields,
      status: KEYWORD_STATUS.active,
      activePostCount: 0,
      soleActiveKeywordPostCount: 0,
      createdOn: stamp,
      createdBy: actor,
      lastUpdatedOn: stamp,
      lastUpdatedBy: actor,
    };
    working.push(saved);
    created.push(saved);
  });
  return working;
}

export function mergeKeywordsAfterBatchCreate(
  keywords,
  {
    tradeCategoryId,
    tradeCategoryName,
    subcategoryId,
    subcategoryName,
    blocks = [],
    poolBlocks = [],
    subcategorySections = null,
  },
  { actor = DEFAULT_ACTOR } = {},
) {
  const stamp = formatKeywordAuditDate();
  const created = [];
  let working = [...keywords];

  working = appendCreatedKeywords(
    working,
    created,
    poolBlocks,
    {
      tradeCategoryId,
      tradeCategoryName,
      subcategoryId: '',
      subcategoryName: 'Pool',
      isPool: true,
    },
    stamp,
    actor,
  );

  const sections =
    subcategorySections ||
    (subcategoryId || blocks.length
      ? [{ subcategoryId, subcategoryName, blocks }]
      : []);

  sections.forEach((section) => {
    working = appendCreatedKeywords(
      working,
      created,
      section.blocks || section.keywords || [],
      {
        tradeCategoryId,
        tradeCategoryName,
        subcategoryId: section.subcategoryId,
        subcategoryName: section.subcategoryName,
        isPool: false,
      },
      stamp,
      actor,
    );
  });

  created.forEach((keyword) => {
    try {
      const iso = new Date().toISOString();
      recordChangeHistory({
        sourceChangeRef: `keywords::${keyword.id}::Add::${iso}`,
        sourceModuleRef: 'keywords_management',
        sourceItemRef: keyword.id,
        module: 'Keywords',
        changedItem: keyword.keyword || keyword.id,
        itemId: keyword.id,
        changeType: 'Add',
        previousValues: CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM,
        newValues: {
          Keyword: keyword.keyword,
          TradeCategory: keyword.tradeCategoryName || keyword.tradeCategoryId,
          Subcategory: keyword.subcategoryName || keyword.subcategoryId || 'Pool',
        },
        changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
        changedBy: actor,
        changedOnIso: iso,
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
  });

  return { keywords: working, created };
}

export function mergeKeywordsAfterCategoryEdit(
  keywords,
  form,
  { actor = DEFAULT_ACTOR, baselineForm = null } = {},
) {
  const stamp = formatKeywordAuditDate();
  const updates = new Map();

  const collectUpdates = (blocks = []) => {
    blocks.forEach((block) => {
      if (!block.id) return;
      const nextName = trimKeyword(block.keyword);
      const baseline =
        (baselineForm?.poolBlocks || []).find((b) => b.id === block.id) ||
        (baselineForm?.subcategorySections || [])
          .flatMap((section) => section.keywords || [])
          .find((b) => b.id === block.id);
      const prevName = trimKeyword(baseline?.keyword);
      if (nextName && nextName !== prevName) {
        updates.set(block.id, nextName);
      }
    });
  };

  collectUpdates(form.poolBlocks || []);
  (form.subcategorySections || []).forEach((section) => collectUpdates(section.keywords || []));

  if (updates.size === 0) {
    return { keywords: [...keywords], changed: false };
  }

  const next = keywords.map((record) => {
    if (!updates.has(record.id)) return record;
    return {
      ...record,
      keyword: updates.get(record.id),
      lastUpdatedOn: stamp,
      lastUpdatedBy: actor,
    };
  });

  return { keywords: next, changed: true };
}

export function mergeKeywordAfterSave(keywords, formData, { isNew = false, actor = DEFAULT_ACTOR } = {}) {
  const stamp = formatKeywordAuditDate();
  const trimmed = trimKeyword(formData.keyword);

  if (isNew) {
    const { keywords: next } = mergeKeywordsAfterBatchCreate(
      keywords,
      {
        tradeCategoryId: formData.tradeCategoryId,
        tradeCategoryName: formData.tradeCategoryName,
        subcategoryId: formData.subcategoryId,
        subcategoryName: formData.subcategoryName,
        blocks: [{ localKey: 'single', keyword: trimmed }],
      },
      { actor },
    );
    return next;
  }

  return keywords.map((k) => {
    if (k.id !== formData.id) return k;
    const updated = {
      ...k,
      keyword: trimmed,
      lastUpdatedOn: stamp,
      lastUpdatedBy: actor,
    };
    if (k.keyword !== trimmed) {
      try {
        const iso = new Date().toISOString();
        recordChangeHistory({
          sourceChangeRef: `keywords::${k.id}::Edit::${iso}`,
          sourceModuleRef: 'keywords_management',
          sourceItemRef: k.id,
          module: 'Keywords',
          changedItem: trimmed || k.id,
          itemId: k.id,
          changeType: 'Edit',
          previousValues: { Keyword: k.keyword },
          newValues: { Keyword: trimmed },
          changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
          changedBy: actor,
          changedOnIso: iso,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
    return updated;
  });
}

/** Payload Configuration — Keywords / downstream modules may receive from a source record. */
export function getKeywordConfigurationHandoff(record) {
  if (!record?.id) return null;
  return {
    keywordId: record.id,
    keyword: record.keyword,
    status: record.status,
    subcategoryId: record.subcategoryId,
    tradeCategoryId: record.tradeCategoryId,
  };
}
